/**
 * Glue code for htmx-driven listings (frontend/views/listing/_grid.php). htmx handles
 * the request and DOM swap itself; there is no separate JS grid module here. Filter
 * markup mirrors what ajax.js::drawFilters() used to build (.filter-item,
 * .js-filter-itemwrap, .custom-checkbox-wrapper), reusing the existing styles and the
 * delegated handlers already wired up in app.js::filters(). That function runs
 * unconditionally on every page via ajax.js (ajaxApp.init -> grid.init -> app.filters()),
 * regardless of whether any [prAjaxGrid="true"] element exists, so it must not be called
 * again here - doing so would double-bind the delegated handlers.
 *
 * Multi-select values (e.g. "Авторы") reach the server in the browser's native format,
 * "lecturer[]=5&lecturer[]=7" - PHP collects this into an array on its own, and
 * FilterUrlParser::getUrlMultipleAttributeValues() already understands it directly.
 */

/**
 * app.js submits "Apply"/"Clear" via $form.trigger('submit'). jQuery's trigger('submit')
 * on a form calls the native HTMLFormElement.prototype.submit() method directly, which
 * per spec bypasses the submitted event entirely - so nothing (not htmx, not a capture-phase
 * preventDefault) can intercept it, causing a real page reload instead of a htmx request.
 * The only fix is to patch submit() itself: for htmx forms, fire htmx.trigger(form, 'submit')
 * instead, which dispatches a real event htmx can handle correctly.
 */
const nativeFormSubmit = HTMLFormElement.prototype.submit
HTMLFormElement.prototype.submit = function () {
  if (this.matches('[hx-get], [hx-post]')) {
    htmx.trigger(this, 'submit')
    return
  }
  nativeFormSubmit.call(this)
}

// select2 does not survive an outerHTML swap - reinitialize it after every htmx swap.
document.addEventListener('htmx:afterSwap', function (evt) {
  app.customSelects()

  // uk-grid="masonry: true" computes each card's column width from the container's
  // offsetWidth at connection time. UIkit auto-boots the swapped-in grid element via its
  // own MutationObserver, but that can fire before the browser has settled layout for
  // the new subtree, so the very first computation sometimes measures a near-zero width
  // and renders every card full-width. Forcing an explicit update() after the swap
  // guarantees a recomputation against the now-settled layout.
  //
  // evt.detail.target is the *original* swap target. For hx-swap="beforeend" (the
  // "Показать ещё" case) that element is still attached, so updating it directly works.
  // For hx-swap="outerHTML" (the filter-form case) htmx removes that exact node from the
  // document and inserts the new content in its place, so evt.detail.target is a detached
  // node by the time this handler runs - updating it is a no-op and the real, newly
  // inserted grid never gets its forced recompute. The swapped-in element keeps the same
  // id either way, so re-querying the live DOM by that id finds the right node in both cases.
  if (window.UIkit) {
    const swappedTarget = evt.detail.target
    const liveTarget = (swappedTarget && swappedTarget.id && document.getElementById(swappedTarget.id))
      || swappedTarget
    if (liveTarget && liveTarget.isConnected) {
      UIkit.update(liveTarget)
    } else {
      UIkit.update()
    }
  }

  // Calendar day cells with 2+ events render a .slider.owl-carousel wrapper (see
  // frontend/views/calendar-events/calendar.php). app.init() only calls app.sliders()
  // once on initial page load, so swapped-in day cells from a month/filter change need
  // it re-applied here; it no-ops on pages with no .slider element.
  app.sliders()
})

/**
 * Same loader as /courses (.pr-loader / app.showLoader) - the styles already exist in
 * the shared style.css, this only inserts/removes the overlay itself, during a htmx
 * request (filter/pagination fetch), via htmx:beforeRequest/afterRequest. hx-swap="outerHTML"
 * replaces the grid container wholesale, so on success the loader disappears along with
 * the old content anyway; the explicit removal on htmx:afterRequest only guards against
 * an error response (no swap means the overlay would otherwise stay forever).
 *
 * Deliberately NOT shown on initial page load: cards are already in the SSR markup and
 * visible immediately, so covering them with an opaque loader (.pr-loader has its own
 * background) for any amount of time is a pure regression, not a fix for anything -
 * there is no async work happening at that point for a loader to mask.
 */
function showGridLoader (container) {
  if (!container || container.querySelector('.pr-loader')) {
    return
  }
  const assetsFolder = (window.jsConfig && window.jsConfig.assetsFolder) || '/resources/'
  const logo = document.createElement('div')
  logo.className = 'pr-loader__logo'
  const img = document.createElement('img')
  img.src = assetsFolder + 'img/loader-logo.webp'
  img.width = 41
  img.height = 41
  img.alt = ''
  logo.appendChild(img)
  const loader = document.createElement('div')
  loader.className = 'pr-loader'
  loader.appendChild(logo)
  container.appendChild(loader)
}

function hideGridLoader (container) {
  const loader = container && container.querySelector('.pr-loader')
  if (!loader) {
    return
  }
  setTimeout(function () {
    loader.remove()
  }, 800)
}

// The grid-container ancestor of the element htmx swapped is what showGridLoader/
// hideGridLoader/scrollToGridTop all operate on; beforeRequest and afterRequest both need
// it resolved the same way, so it's one lookup instead of repeating closest() in each.
function resolveGridContainer (evt) {
  return evt.detail.target && evt.detail.target.closest('.grid-container')
}

// "Показать ещё" (hx-swap="beforeend" on the button) appends the next page's cards
// without touching what's already on screen, unlike a filter/pagination-link request
// (hx-swap="outerHTML"), which replaces the whole grid. Covering the grid-container with
// the opaque .pr-loader for a beforeend request would hide the already-loaded cards for
// no reason, then reveal old+new together once the loader comes off - exactly the "cards
// disappear, then all reappear at once" symptom this guard prevents.
function isLoadMoreRequest (evt) {
  return evt.detail.elt && evt.detail.elt.getAttribute('hx-swap') === 'beforeend'
}

// Numbered page links (and the first/last-page shortcuts) live in _pagination_list.php's
// .uk-pagination and jump the whole grid to a different page (hx-swap="outerHTML"). The
// click itself usually happens at the bottom of a tall grid, so without an explicit scroll
// the page lands on new content the user isn't looking at. Filter-form changes go through
// the same outerHTML swap but start from an element already near the top of the grid, so
// they're deliberately excluded here - only pagination clicks need the scroll.
function isPaginationRequest (evt) {
  return !!(evt.detail.elt && evt.detail.elt.closest('.uk-pagination'))
}

function scrollToGridTop (container) {
  if (!container) {
    return
  }
  const top = container.getBoundingClientRect().top + window.pageYOffset - 20
  window.scrollTo({ top: top, behavior: 'smooth' })
}

document.addEventListener('htmx:beforeRequest', function (evt) {
  if (isLoadMoreRequest(evt)) {
    return
  }
  const container = resolveGridContainer(evt)
  showGridLoader(container)
  // Scrolling starts as soon as the click fires (not after the swap completes) so the
  // motion overlaps with the request/loader instead of happening as a jarring jump once
  // the new page's cards land.
  if (isPaginationRequest(evt)) {
    scrollToGridTop(container)
  }
})

document.addEventListener('htmx:afterRequest', function (evt) {
  if (isLoadMoreRequest(evt)) {
    return
  }
  hideGridLoader(resolveGridContainer(evt))
})
